import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, CaretRight } from '@phosphor-icons/react';
import { getStores } from '../../services/customerApi';
import { useCustomer, PageWrap } from './CustomerApp';
import { toast } from 'sonner';

export default function StoreSelect() {
  const navigate = useNavigate();
  const { selectStore, selectedStore } = useCustomer();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const res = await getStores();
      setStores(res.data.stores || []);
    } catch (err) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStore = (store) => {
    selectStore(store);
    toast.success(`Ordering from ${store.name}`);
    navigate('/order');
  };

  return (
    <PageWrap>
      <div className="min-h-screen pb-24" data-testid="store-select-page">
        {/* Header */}
        <div className="relative h-48 overflow-hidden">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/93ec89ee-cba6-4838-8703-36ee1dee7183/images/8032d1cd316467fbcde2ed804b5075387b4f2f17bd6d94a986ddf452b5777683.png"
            alt="Pizzeria"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/70 to-transparent" />
          <div className="absolute bottom-4 left-5">
            <h1 className="text-2xl font-black text-[#FFFFFF] tracking-tight">Select a Store</h1>
            <p className="text-xs text-[#FFFFFF]/70 mt-0.5">Choose your nearest SSPICE outlet</p>
          </div>
        </div>

        {/* Store List */}
        <div className="px-4 pt-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-3 border-[#E21836] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stores.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280]">No stores available</div>
          ) : (
            stores.map((store, i) => (
              <motion.button
                key={store.id}
                data-testid={`store-card-${i}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => handleSelectStore(store)}
                className={`w-full flex items-center gap-3 p-4 border rounded-2xl text-left hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ${selectedStore?.id === store.id ? 'bg-[#E21836]/10 border-[#E21836]' : 'bg-[#F8FAFC] border-[#E5E7EB]'}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#E21836]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} weight="duotone" className="text-[#E21836]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#111827] text-sm truncate">{store.name}</h3>
                  <p className="text-xs text-[#6B7280] truncate mt-0.5">{store.address}</p>
                  {selectedStore?.id === store.id && (
                    <span className="text-[10px] font-bold text-[#E21836] uppercase tracking-wide">Currently selected</span>
                  )}
                </div>
                <CaretRight size={16} className="text-[#6B7280] flex-shrink-0" />
              </motion.button>
            ))
          )}
        </div>
      </div>
    </PageWrap>
  );
}
